/**
 * Wanderlust Jewelry - global behaviours, loaded on every page from layout/theme.liquid.
 * Plain modules with no Horizon imports so upstream theme updates cannot break them.
 *
 *   [data-wj-open="dialog-id"]   opens a <dialog> as a modal drawer; [data-wj-close] or backdrop click closes
 *   <wj-wishlist-button>         heart toggle, stored per browser (localStorage) - see wishlist note below
 *   [data-wj-wishlist-count]     live count badge anywhere in the theme (header icon)
 *   <wj-delivery-estimate>       "Order within 3h 12m for dispatch today" with SA public holidays
 *   <wj-installments>            "or 4 × R112.50 interest-free" next to the price
 */

/* ---------- drawers ---------- */
document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);

  const opener = target.closest('[data-wj-open]');
  if (opener) {
    const dialog = document.getElementById(opener.getAttribute('data-wj-open'));
    if (dialog instanceof HTMLDialogElement && !dialog.open) {
      dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      dialog.addEventListener('close', () => (document.documentElement.style.overflow = ''), { once: true });
    }
    return;
  }

  if (target.closest('[data-wj-close]')) {
    target.closest('dialog')?.close();
    return;
  }

  // Click on the backdrop: the event target is the dialog element itself.
  if (target instanceof HTMLDialogElement && target.classList.contains('wj-drawer')) {
    const rect = target.getBoundingClientRect();
    const outside =
      event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    if (outside) target.close();
  }
});

/* ---------- wishlist ----------
 * Stored in localStorage as product handles so it works for guests with zero apps.
 * DECISION PENDING (docs/DECISIONS.md D-07): sync to customer accounts via an app or a customer metafield
 * once Wanderlust Jewelry's account strategy is settled. The public API here (wj:wishlist-change event) stays the same.
 */
const WISHLIST_KEY = 'wj:wishlist';

export const wishlist = {
  /** @returns {string[]} */
  all() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    } catch {
      return [];
    }
  },
  /** @param {string} handle */
  has(handle) {
    return this.all().includes(handle);
  },
  /** @param {string} handle */
  toggle(handle) {
    const items = this.all();
    const index = items.indexOf(handle);
    if (index === -1) items.unshift(handle);
    else items.splice(index, 1);
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(items.slice(0, 100)));
    } catch {
      /* storage full or blocked - toggle still reflects in this page view */
    }
    document.dispatchEvent(new CustomEvent('wj:wishlist-change', { detail: { items, handle, added: index === -1 } }));
    return index === -1;
  },
};
window.WJ = Object.assign(window.WJ || {}, { wishlist });

class WjWishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.handle = this.dataset.handle;
    this.render();
    // Guard: the element can be re-connected (moved by carousels / morph) and a second click listener would
    // toggle twice per click, i.e. do nothing.
    if (!this.ready) {
      this.ready = true;
      this.button?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        wishlist.toggle(this.handle);
      });
    }
    this.onChange = () => this.render();
    document.addEventListener('wj:wishlist-change', this.onChange);
  }

  disconnectedCallback() {
    document.removeEventListener('wj:wishlist-change', this.onChange);
  }

  render() {
    const saved = wishlist.has(this.handle);
    this.toggleAttribute('saved', saved);
    this.button?.setAttribute('aria-pressed', String(saved));
  }
}
if (!customElements.get('wj-wishlist-button')) customElements.define('wj-wishlist-button', WjWishlistButton);

function renderWishlistCounts() {
  const count = wishlist.all().length;
  document.querySelectorAll('[data-wj-wishlist-count]').forEach((el) => {
    el.textContent = count ? String(count) : '';
    el.hidden = count === 0;
  });
}
document.addEventListener('wj:wishlist-change', renderWishlistCounts);
document.addEventListener('DOMContentLoaded', renderWishlistCounts);
renderWishlistCounts();

/* ---------- delivery estimate ----------
 * Business days only, skipping weekends and the public holidays listed in data-holidays (YYYY-MM-DD, comma separated).
 * Times are evaluated in Africa/Johannesburg regardless of the shopper's device clock.
 */
const TZ = 'Africa/Johannesburg';

function zonedNow() {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-ZA', {
      timeZone: TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date())
      .map((p) => [p.type, p.value])
  );
  return {
    date: new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day)),
    minutes: +parts.hour * 60 + +parts.minute,
  };
}

const iso = (d) => d.toISOString().slice(0, 10);

class WjDeliveryEstimate extends HTMLElement {
  connectedCallback() {
    // Commas, spaces or new lines all separate dates (merchants often paste one date per line).
    this.holidays = new Set((this.dataset.holidays || '').split(/[\s,;]+/).filter(Boolean));
    window.clearInterval(this.timer);
    this.render();
    this.timer = window.setInterval(() => this.render(), 60_000);
    this.resizeDays = this.resizeDays || 0;
    this.engraveDays = this.engraveDays || 0;
    this.onResize = (event) => {
      this.resizeDays = event.detail?.on ? Number(event.detail.leadDays) || 0 : 0;
      this.render();
    };
    this.onEngrave = (event) => {
      this.engraveDays = event.detail?.on ? Number(event.detail.leadDays) || 0 : 0;
      this.render();
    };
    document.addEventListener('wj:resize-change', this.onResize);
    document.addEventListener('wj:engraving-change', this.onEngrave);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
    document.removeEventListener('wj:resize-change', this.onResize);
    document.removeEventListener('wj:engraving-change', this.onEngrave);
  }

  /** @param {Date} d */
  isBusinessDay(d) {
    const day = d.getUTCDay();
    return day !== 0 && day !== 6 && !this.holidays.has(iso(d));
  }

  /** @param {Date} from @param {number} days */
  addBusinessDays(from, days) {
    const d = new Date(from);
    let added = 0;
    while (added < days) {
      d.setUTCDate(d.getUTCDate() + 1);
      if (this.isBusinessDay(d)) added++;
    }
    return d;
  }

  render() {
    const cutoff = (Number(this.dataset.cutoffHour) || 13) * 60;
    const minDays = Number(this.dataset.minDays) || 2;
    const maxDays = Number(this.dataset.maxDays) || 4;
    const { date, minutes } = zonedNow();

    let dispatch = new Date(date);
    const dispatchToday = this.isBusinessDay(date) && minutes < cutoff;
    if (!dispatchToday) dispatch = this.addBusinessDays(date, 1);
    // Resizing (WJ · Ring resize option) and engraving (WJ · Engraving) add studio working days before dispatch.
    // Added together: no source says both happen in the same days, so never promise the shorter time.
    const extraDays = this.resizeDays + this.engraveDays;
    if (extraDays > 0) dispatch = this.addBusinessDays(dispatch, extraDays);

    const from = this.addBusinessDays(dispatch, minDays);
    const to = this.addBusinessDays(dispatch, maxDays);
    const fmt = new Intl.DateTimeFormat('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });

    const countdown = this.querySelector('[data-countdown]');
    if (countdown) {
      if (extraDays > 0) {
        const work = this.resizeDays && this.engraveDays ? 'Resized and engraved' : this.engraveDays ? 'Engraved' : 'Resized';
        countdown.textContent = `${work} for you, then dispatched by ${fmt.format(dispatch)}.`;
      } else if (dispatchToday) {
        const left = cutoff - minutes;
        const hours = Math.floor(left / 60);
        const time = hours ? `${hours}h ${left % 60}m` : `${left} min`;
        countdown.textContent = `Order within ${time} for same-day dispatch.`;
      } else {
        countdown.textContent = `Ships ${fmt.format(dispatch)}.`;
      }
    }
    const window_ = this.querySelector('[data-window]');
    if (window_) window_.textContent = `${fmt.format(from)} – ${fmt.format(to)}`;
  }
}
if (!customElements.get('wj-delivery-estimate')) customElements.define('wj-delivery-estimate', WjDeliveryEstimate);

/* ---------- buy-now-pay-later instalments ----------
 * Horizon 4 dispatches the Shopify standard event `shopify:product:select`; its `promise` resolves once the
 * new variant has been fetched, with the variant JSON (price in cents) at detail.resource.
 */
/**
 * Formats cents with the shop's own money format (Liquid `shop.money_format`, e.g. "R{{amount}}"), so prices
 * written by JavaScript match prices written by Liquid. Supports Shopify's common placeholders.
 * @param {number} cents
 * @param {string} [format]
 */
export function formatMoney(cents, format = 'R{{amount}}') {
  const group = (value, decimals, thousands, decimal) => {
    const [whole, frac] = (value / 100).toFixed(decimals).split('.');
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, kind) => {
    switch (kind) {
      case 'amount_no_decimals':
        return group(cents, 0, ',', '.');
      case 'amount_with_comma_separator':
        return group(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return group(cents, 0, '.', ',');
      case 'amount_with_space_separator':
        return group(cents, 2, ' ', ',');
      default:
        return group(cents, 2, ',', '.');
    }
  });
}

class WjInstallments extends HTMLElement {
  connectedCallback() {
    this.update(Number(this.dataset.price));
    // event.product.id is normalised to a gid:// string by the standard-events library, so match on the raw id
    // Horizon puts in result.detail.productId (same filter as Horizon's product-price.js).
    this.onVariant = (event) => {
      const source = event?.target;
      if (!(source instanceof Element) || (source.closest('product-card') && !source.closest('product-card')?.contains(this))) return;
      event.promise
        ?.then((result) => {
          const detail = result?.detail;
          if (!detail) return;
          if (detail.newProduct) this.dataset.productId = String(detail.newProduct.id);
          else if (detail.productId && this.dataset.productId && String(detail.productId) !== this.dataset.productId) return;
          const price = detail.resource?.price;
          if (typeof price === 'number') this.update(price);
        })
        .catch(() => {});
    };
    document.addEventListener('shopify:product:select', this.onVariant);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:product:select', this.onVariant);
  }

  /** @param {number} cents */
  update(cents) {
    // D-158: during Black November the instalment is worked out on the price after this piece's discount.
    cents = (window.wjBnPrice || ((c) => c))(cents, this.dataset.wjBnPcts);
    const n = Number(this.dataset.count) || 4;
    const min = Number(this.dataset.min) || 0;
    const max = Number(this.dataset.max) || Infinity;
    const amount = this.querySelector('[data-amount]');
    this.hidden = !(cents >= min && cents <= max);
    if (amount) {
      amount.textContent = formatMoney(Math.round(cents / n), this.dataset.moneyFormat);
    }
  }
}
if (!customElements.get('wj-installments')) customElements.define('wj-installments', WjInstallments);

/* ---------- cart attributes (gift wrap / gift message) ----------
 * Saves on change so the values survive Horizon re-rendering the cart summary. Checkout also posts them.
 */
class WjCartAttributes extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => this.save(event));
    this.addEventListener('input', (event) => {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.save(event), 800);
    });
  }

  /** @param {Event} event */
  save(event) {
    const field = /** @type {HTMLInputElement | HTMLTextAreaElement} */ (event.target);
    const match = field.name?.match(/^attributes\[(.+)\]$/);
    if (!match) return;
    const value = field instanceof HTMLInputElement && field.type === 'checkbox' ? (field.checked ? 'Yes' : 'No') : field.value;
    const root = window.Shopify?.routes?.root || '/';
    fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [match[1]]: value } }),
    }).catch(() => {
      /* checkout submit posts the same fields, so a failed background save is not fatal */
    });
  }
}
if (!customElements.get('wj-cart-attributes')) customElements.define('wj-cart-attributes', WjCartAttributes);

/* ---------- paid gift wrap (snippets/wj-cart-extras.liquid) ----------
 * Up to three gift-wrap products (premium box / paper / bag; Theme settings > Wanderlust Jewelry: features). Choosing one
 * removes any wrap already in the cart and adds the chosen one; "No gift wrap" just removes. Uses Shopify's standard
 * cart action so Horizon re-renders the bag; falls back to the AJAX cart + reload.
 */
class WjGiftWrap extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => {
      const input = /** @type {HTMLInputElement} */ (event.target);
      if (input.name === 'wj-gift-wrap') this.choose(input.value);
    });
  }

  /** @param {string} variantId - empty for "No gift wrap" */
  async choose(variantId) {
    const existing = (this.dataset.lines || '').split(',').filter(Boolean);
    const lines = existing.map((id) => ({ id, quantity: 0 }));
    if (variantId) lines.push({ merchandiseId: variantId, quantity: 1 });
    if (!lines.length) return;
    const inputs = [...this.querySelectorAll('input')];
    inputs.forEach((i) => (i.disabled = true));
    try {
      const actions = window.Shopify?.actions;
      if (actions?.updateCart) {
        await actions.updateCart({ lines });
      } else {
        const root = window.Shopify?.routes?.root || '/';
        const post = (path, body) =>
          fetch(`${root}cart/${path}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
        if (existing.length) await post('update', { updates: Object.fromEntries(existing.map((k) => [k, 0])) });
        if (variantId) await post('add', { items: [{ id: Number(variantId), quantity: 1 }] });
        window.location.reload();
      }
    } finally {
      inputs.forEach((i) => (i.disabled = false));
    }
  }
}
if (!customElements.get('wj-gift-wrap')) customElements.define('wj-gift-wrap', WjGiftWrap);

/* ---------- Google reviews on the product page (D-140) ----------
 * Reviews are entered once, as blocks of WJ · Google reviews on the homepage. A copy of that section with no
 * blocks of its own renders <wj-greviews-mirror>, which fetches the homepage when it nears the viewport and shows
 * the homepage section's markup. Nothing renders if the homepage section is missing or the fetch fails.
 */
class WjGreviewsMirror extends HTMLElement {
  connectedCallback() {
    if (this.loaded) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: '800px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async load() {
    this.loaded = true;
    try {
      const response = await fetch(this.dataset.src || '/', { credentials: 'same-origin' });
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const section = doc.querySelector('.wj-greviews')?.closest('.shopify-section');
      if (section) this.replaceChildren(...[...section.children].map((node) => document.importNode(node, true)));
    } catch {
      /* reviews are optional: leave the space empty */
    }
  }
}
if (!customElements.get('wj-greviews-mirror')) customElements.define('wj-greviews-mirror', WjGreviewsMirror);

/* ---------- Delivery and risk tick box (D-146, D-147) ----------
 * snippets/wj-delivery-risk.liquid sits above Checkout in the bag and above Buy it now on the product page. One tick
 * counts for every box on the page (same shopper, same version). Until ticked, Checkout is blocked here and Buy it
 * now / express buttons (Shopify's own frames, which cannot be blocked from outside) are made inert. The tick is
 * saved as cart attributes; on the product page also as hidden line-item properties, because Buy it now skips the
 * cart. Horizon re-renders the cart summary after every change, so the bag box gets its state back from the server.
 */
const WJ_RISK_KEY = 'Delivery risk accepted';

class WjDeliveryRisk extends HTMLElement {
  connectedCallback() {
    this.agree = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-ref="agree"]'));
    this.sheet = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-ref="sheet"]'));
    if (this.sheet) this.initSheet();
    else this.agree?.addEventListener('change', () => WjDeliveryRisk.setAll(this.agree?.checked === true));
    this.apply(this.hasAttribute('data-accepted'), this.dataset.at || '');
  }

  /* Product page (Justin, 27 Oct 2026): no visible box. A transparent gate covers Buy it now until accepted;
   * tapping it opens the sheet, and Continue (only once ticked) accepts and carries on with Buy it now. */
  initSheet() {
    const sheet = /** @type {HTMLDialogElement} */ (this.sheet);
    const gate = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-ref="gate"]'));
    const next = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-ref="continue"]'));
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-ref="error"]'));
    /** @type {{x: number, y: number} | null} */
    let tap = null;

    gate?.addEventListener('click', (event) => {
      tap = event.detail > 0 ? { x: event.clientX, y: event.clientY } : null;
      if (this.agree) this.agree.checked = false;
      next?.setAttribute('aria-disabled', 'true');
      if (error) error.hidden = true;
      sheet.showModal();
    });
    this.agree?.addEventListener('change', () => {
      next?.setAttribute('aria-disabled', String(!this.agree?.checked));
      if (error && this.agree?.checked) error.hidden = true;
    });
    this.querySelector('[data-ref="close"]')?.addEventListener('click', () => sheet.close());
    sheet.addEventListener('click', (event) => {
      if (event.target === sheet) sheet.close(); // tap on the backdrop
    });
    sheet.addEventListener('close', () => {
      if (!this.hasAttribute('data-accepted')) gate?.focus();
    });
    next?.addEventListener('click', () => {
      if (!this.agree?.checked) {
        if (error) error.hidden = false;
        this.agree?.focus();
        return;
      }
      WjDeliveryRisk.setAll(true);
      sheet.close();
      this.proceed(tap);
    });
  }

  /** After Continue: press Buy it now for the shopper, unless they had tapped an express button (Apple Pay etc.
   * need their own tap, so those are only unlocked). @param {{x: number, y: number} | null} tap */
  proceed(tap) {
    const guarded = this.guarded;
    if (!guarded) return;
    const hit = tap ? document.elementFromPoint(tap.x, tap.y) : null;
    if (hit && guarded.contains(hit) && !this.contains(hit) && !hit.closest('shopify-buy-it-now-button')) return;
    const host = guarded.querySelector('shopify-buy-it-now-button');
    const button = /** @type {HTMLElement | null} */ (
      host?.shadowRoot?.querySelector('button') || host?.querySelector('button') || null
    );
    if (button) button.click();
    else /** @type {HTMLElement | null} */ (guarded.querySelector('button, [role="button"]'))?.focus();
  }

  /** The buttons this box guards: the Buy it now block it sits in (product page) or the bag's checkout row. */
  get guarded() {
    if (this.sheet) return this.closest('.accelerated-checkout-block');
    const next = this.nextElementSibling;
    return next instanceof HTMLElement ? next : null;
  }

  /** @param {boolean} on @param {string} at */
  apply(on, at) {
    this.toggleAttribute('data-accepted', on);
    if (this.agree) this.agree.checked = on;
    if (on) this.dataset.at = at;
    const prop = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-ref="prop"]'));
    const propAt = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-ref="prop-at"]'));
    if (prop) prop.disabled = !on;
    if (propAt) {
      propAt.disabled = !on;
      propAt.value = on ? at : '';
    }
    // Product page: Buy it now / express buttons are inert (no keyboard or tap route past the gate) until accepted.
    const guarded = this.guarded;
    if (this.sheet && guarded) {
      for (const child of guarded.children) if (child !== this) /** @type {HTMLElement} */ (child).inert = !on;
    }
    const error = this.querySelector('[data-ref="error"]');
    if (error && on) /** @type {HTMLElement} */ (error).hidden = true;
  }

  /** @param {boolean} on */
  static setAll(on) {
    const at = on ? new Date().toISOString() : '';
    document.querySelectorAll('wj-delivery-risk').forEach((box) => /** @type {WjDeliveryRisk} */ (box).apply(on, at));
    const version = /** @type {HTMLElement | null} */ (document.querySelector('wj-delivery-risk'))?.dataset.version || 'yes';
    const root = window.Shopify?.routes?.root || '/';
    fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [WJ_RISK_KEY]: on ? version : '', [`${WJ_RISK_KEY} at`]: at } }),
    }).catch(() => {});
  }

  warn() {
    const error = this.querySelector('[data-ref="error"]');
    if (error) /** @type {HTMLElement} */ (error).hidden = false;
    this.agree?.focus();
  }
}
if (!customElements.get('wj-delivery-risk')) customElements.define('wj-delivery-risk', WjDeliveryRisk);

// Block every route to checkout from the bag until its box is ticked (click, Enter in the cart form, submit).
const blockCheckout = (/** @type {Event} */ event) => {
  const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
  const submitter = /** @type {HTMLElement | null} */ (/** @type {SubmitEvent} */ (event).submitter || null);
  const button =
    event.type === 'click' ? target?.closest('[name="checkout"]') : submitter?.getAttribute('name') === 'checkout' ? submitter : null;
  if (!button) return;
  const box = button.closest('.cart__ctas')?.previousElementSibling;
  if (!(box instanceof WjDeliveryRisk) || box.hasAttribute('data-accepted')) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  box.warn();
};
document.addEventListener('click', blockCheckout, true);
document.addEventListener('submit', blockCheckout, true);

/* ---------- free first resize after discount (D-158) ----------
 * "Free first resize" notes carry data-wj-free-resize (price) and data-wj-bn-pcts (snippets/wj-bn.liquid, mode
 * 'attrs'). While Black November takes the piece to R2,000 or under, the resize is paid, so the note goes.
 */
(() => {
  let min = 0;
  try {
    min = (Number(JSON.parse(document.getElementById('wj-fee-config')?.textContent || '{}').freeResizeMin) || 0) * 100;
  } catch {}
  const price = window.wjBnPrice || ((c) => c);
  document.querySelectorAll('[data-wj-free-resize]').forEach((el) => {
    const el2 = /** @type {HTMLElement} */ (el);
    if (!(price(Number(el2.dataset.wjFreeResize), el2.dataset.wjBnPcts) > min)) el2.style.display = 'none';
  });
})();

/* ---------- cart count: pieces, not fee units (site audit 21 Oct W1, Black November plan W2) ----------
 * One ring with LOVE engraved is 1 piece + 4 "per character" fee units, and Horizon's badge said 5. The badges are
 * Horizon's own (cart-icon.js writes cart.totalQuantity), so instead of editing them this corrects any count that
 * doesn't match the pieces in the bag: fee products (handles from #wj-fee-config) don't count. Checkout's own
 * "N items" is Shopify's and can't be changed from the theme.
 */
(() => {
  /** @type {string[]} */
  let fees = [];
  try {
    fees = JSON.parse(document.getElementById('wj-fee-config')?.textContent || '{}').handles || [];
  } catch {}
  fees = fees.filter(Boolean);
  if (!fees.length) return;

  const COUNTS = '[ref="cartBubbleCount"], .theme-drawer__badge';
  /** @type {number | null} */
  let pieces = null;
  let timer = 0;

  // Returns true when some badge disagrees with the last known piece count (fixing it when that count is known).
  const apply = () => {
    let stale = false;
    document.querySelectorAll(COUNTS).forEach((el) => {
      const text = el.textContent.trim();
      if (text === '' || text === String(pieces)) return;
      stale = true;
      if (pieces !== null) el.textContent = String(pieces);
    });
    return stale;
  };

  const refresh = async () => {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
      pieces = cart.items.filter((i) => !fees.includes(i.handle)).reduce((n, i) => n + i.quantity, 0);
      apply();
    } catch {}
  };

  // Horizon rewrites the badge after every bag change; a count that disagrees means the bag changed, so re-read it.
  new MutationObserver(() => {
    if (!apply()) return;
    clearTimeout(timer);
    timer = window.setTimeout(refresh, 120);
  }).observe(document.body, { subtree: true, childList: true, characterData: true });
  refresh();
})();

/* ---------- phone: price + Add to cart on the first screen (Justin, 3 Nov 2026: "Yes, fix it now") ----------
 * On a 375x812 phone the ring size, resize and engraving options push Add to cart to ~1,100 px, and the cookie notice
 * covers the bottom 128 px on a first visit. Until the real Add to cart is on screen, a slim bar shows the price and an
 * Add to cart that presses the REAL button, so ring size, engraving and every other check run exactly as before.
 * It sits just above the cookie notice while that is open, and hides once the real button is in view or scrolled past
 * (Horizon's own sticky bar takes over from there). Built here, so no Horizon file changes.
 */
(() => {
  const form = document.querySelector('.product-information .product-details product-form-component');
  const real = /** @type {HTMLButtonElement | null} */ (form?.querySelector('[name="add"]') || null);
  const priceBox = document.querySelector('.product-information .product-details product-price [ref="priceContainer"]');
  if (!form || !real || !priceBox) return;
  const phone = window.matchMedia('(max-width: 749px)');

  const bar = document.createElement('div');
  bar.className = 'wj-buybar';
  bar.hidden = true;
  bar.innerHTML =
    '<div class="wj-buybar__info"><div class="wj-buybar__price"></div><button type="button" class="wj-buybar__opt" hidden></button></div>' +
    '<button type="button" class="button wj-buybar__add"></button>';
  document.body.append(bar);
  const price = /** @type {HTMLElement} */ (bar.querySelector('.wj-buybar__price'));
  const opt = /** @type {HTMLButtonElement} */ (bar.querySelector('.wj-buybar__opt'));
  const add = /** @type {HTMLButtonElement} */ (bar.querySelector('.wj-buybar__add'));
  const picker = document.querySelector('.product-information .product-details variant-picker');

  const sync = () => {
    price.innerHTML = priceBox.innerHTML;
    add.textContent = (real.textContent || '').trim() || 'Add to cart';
    add.disabled = real.disabled;
    // Rings: show the chosen size, tap to go to the options (Keep size / Resize / engraving sit there).
    // One piece = one size (single-value option shown as text) or a real choice (legend + checked swatch / select).
    const single = picker?.querySelector('[data-testid="variant-option-single"]');
    const valueEl = single?.querySelector('.variant-option__swatch-value');
    const legend = single
      ? (single.firstChild?.textContent || '').trim()
      : picker?.querySelector('legend')?.textContent?.trim().replace(/:.*$/, '') || '';
    const chosen = /** @type {HTMLInputElement | null} */ (picker?.querySelector('input:checked') || null);
    const select = /** @type {HTMLSelectElement | null} */ (picker?.querySelector('select') || null);
    const value = (valueEl?.textContent || chosen?.value || select?.value || '').trim();
    opt.hidden = !value || !legend;
    if (!opt.hidden) opt.textContent = `${legend} ${value} · change`;
  };
  sync();
  new MutationObserver(sync).observe(priceBox, { childList: true, subtree: true, characterData: true });
  new MutationObserver(sync).observe(real, { childList: true, subtree: true, characterData: true, attributes: true });
  picker?.addEventListener('change', () => setTimeout(sync, 0));
  document.addEventListener('shopify:product:select', () => setTimeout(sync, 400));

  add.addEventListener('click', () => real.click());
  opt.addEventListener('click', () =>
    (document.querySelector('.product-information .product-details .wj-resize') || picker)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  );

  const consent = document.querySelector('wj-consent-bar');
  const place = () => {
    const cover = consent && !consent.hasAttribute('hidden') ? consent.getBoundingClientRect().height : 0;
    bar.style.bottom = `${Math.round(cover)}px`;
    const rect = real.getBoundingClientRect();
    const below = rect.bottom > window.innerHeight - cover; // not reached yet, or hidden under the cookie notice
    bar.hidden = !phone.matches || rect.top < 0 || !below;
  };
  // Direct calls, no requestAnimationFrame: the work is two rect reads, and a frame wait can stall in a background tab.
  const schedule = place;
  addEventListener('scroll', schedule, { passive: true });
  addEventListener('resize', schedule);
  phone.addEventListener('change', schedule);
  new ResizeObserver(schedule).observe(document.body); // images and option blocks loading move the real button
  if (consent) {
    new ResizeObserver(schedule).observe(consent);
    new MutationObserver(schedule).observe(consent, { attributes: true, attributeFilter: ['hidden'] });
  }
  place();
})();
